import React, { ReactNode, useCallback, useRef, FC } from 'react';
import { useRadio, useRadioGroup } from 'react-aria';
import { RadioGroupState, useRadioGroupState } from 'react-stately';
import { LoggableProps, useLogger } from '@direct-frontend/modules-logger';
import { genericMemo } from '@direct-frontend/stdlib';

import { ToggleBase, ToggleProps } from '../ToggleBase/ToggleBase';
import { useStack } from '../../hooks/useStack';
import { getClassNames } from '../../lib/css/className/className';
import { FormFieldControlProps } from '../FormField';
import { HelpHandler } from '../Help';
import { TextTruncationProps } from '../TextGeneric/TextGeneric';
import { TestProps } from '../../internal/types/testTypes';
import { CheckboxSize } from '../Checkbox';

import { RadioboxSize } from './types';
import { Box } from './Box/Box';

type AriaRadioProps = Parameters<typeof useRadio>[0];

export interface RadioProps
    extends AriaRadioProps,
        HelpHandler,
        TestProps,
        TextTruncationProps,
        Omit<ToggleProps, 'size' | 'labelProps'> {
    state: RadioGroupState;
    size: RadioboxSize;
}

const checkboxSizeMap: Record<RadioboxSize, CheckboxSize> = {
    s: 'xs',
    m: 'm',
} as const;

const Radio: FC<RadioProps> = function Radiobox(props) {
    const { state, children, value, isDisabled, size } = props;

    const ref = useRef<HTMLInputElement>(null);
    const { inputProps, labelProps } = useRadio(props, state, ref);

    return (
        <ToggleBase
            {...props}
            size={checkboxSizeMap[size]}
            inputProps={inputProps}
            labelProps={labelProps}
            inputRef={ref}
            label={children}
            role="radio"
        >
            {({ isHovered, isFocused }) => (
                <Box
                    isSelected={state.selectedValue === value}
                    isFocused={isFocused}
                    isHovered={isHovered}
                    isDisabled={isDisabled}
                    size={size}
                />
            )}
        </ToggleBase>
    );
};

type AriaRadioGroupProps = Parameters<typeof useRadioGroup>[0];

export interface RadioboxItem<ValueType extends string = string> extends HelpHandler, TextTruncationProps {
    value: ValueType;
    content: ReactNode;
    disabled?: boolean;
    caption?: ReactNode;
    isBold?: boolean;
}

export interface RadioboxProps<ValueType extends string = string>
    extends TestProps,
        Omit<
            AriaRadioGroupProps,
            'orientation' | 'children' | 'isReadOnly' | 'validationState' | 'isRequired' | 'label' | 'onChange'
        >,
        FormFieldControlProps {
    /**
     * Дополнительный класс для группы
     */
    className?: string;
    /**
     * Список опций для группы
     */
    options: RadioboxItem<ValueType>[];
    /**
     * Параметр для переключения направления, в котором раскладываются элементы Radiobox
     * @default vertical
     */
    orientation?: 'vertical' | 'horizontal';
    /**
     * размер Radiobox
     * @default m
     */
    size?: RadioboxSize;
    /**
     * Обработчик выбора элемента
     * @param value
     */
    onChange?: (value: ValueType) => void;
}

/**
 * Компонент для создания наборов радиобоксов
 */
export const Radiobox = genericMemo(function Radiobox<ValueType extends string = string>(
    props: LoggableProps<RadioboxProps<ValueType>>,
) {
    const { orientation = 'vertical', size = 'm', onChange, logNode } = props;
    const { LogProvider, logClick } = useLogger('Radiobox', logNode);

    const onChangeWithLog = useCallback(
        (value: string) => {
            logClick({
                type: 'Radiobox.RadioButtonClick',
                value,
            });
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- ValueType является подмножеством string, но в кишках onChange захардкожен string
            onChange?.(value as ValueType);
        },
        [logClick, onChange],
    );
    const state = useRadioGroupState({ ...props, onChange: onChangeWithLog });
    const { radioGroupProps } = useRadioGroup({ ...props, onChange: onChangeWithLog }, state);
    const stackClass = useStack({
        type: orientation,
        gap: '16',
    });

    // TODO DIRECT-222878 Починить проблему с клавиатурной навигацией при наличии тултипа
    // https://st.yandex-team.ru/DIRECT-166821
    return (
        <LogProvider>
            <div {...radioGroupProps} className={getClassNames([stackClass, props.className])}>
                {props.options.map(option => (
                    <Radio
                        key={option.value}
                        value={option.value}
                        caption={option.caption}
                        help={option.help}
                        isDisabled={option.disabled || props.isDisabled}
                        isBold={option.isBold}
                        isTruncated={option.isTruncated}
                        maxLines={option.maxLines}
                        size={size}
                        state={state}
                        data-testid={`${props['data-testid'] || 'Radiobox'}.${option.value}`}
                    >
                        {option.content}
                    </Radio>
                ))}
            </div>
        </LogProvider>
    );
});
